import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useCountUp, useSupabasePaginatedQuery, useSupabaseQuery } from "../../../hooks";
import { formatTimeAgo } from "../../../lib/formatTime";
import { PreggaColors } from "../../../theme/colors";
import { DataTable, TableColumn } from "../../ui/DataTable";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { StatusBadge, Badge } from "../../ui/Badge";
import { Card } from "../../ui/Card";
import { Modal } from "../../ui/Modal";
import {
  fetchDoulas,
  fetchDoulaById,
  fetchDoulaConversations,
  fetchDoulaAssignments,
  fetchDoulaKpiCounts,
  unassignClientFromDoula,
  updateDoulaProfile,
  updateDoulaAvailability,
  deactivateDoula,
  reactivateDoula,
  fetchInactiveDoulas,
  type DoulaFilters,
} from "../../../lib/api";
import { friendlyError } from "../../../lib/errors";
import type { DoulaWithProfile, ConversationWithUsers, DoulaAssignmentWithUser } from "../../../types/database";
import { AddDoulaModal } from "./AddDoulaModal";
import { AssignClientsModal } from "./AssignClientsModal";
import {
  Search,
  Users,
  X,
  AlertCircle,
  Pencil,
  Check,
  MessageCircle,
  ToggleRight,
  Trash2,
  AlertTriangle,
  User,
  Plus,
  UserPlus,
  Link2,
  UserMinus,
  Copy,
  RotateCcw,
} from "lucide-react";
import { DetailHeader, TabSelector, Tab } from "../../ui";

interface DoulasViewProps {
  isMobile: boolean;
  subView?: string;
  onNavigateToSubView?: (id: string) => void;
  onGoBack?: () => void;
  onNavigateToUserWithReturn?: (userId: string) => void;
  onNavigateToConversation?: (conversationId: string) => void;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CopyableId({ id }: { id: string }) {
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    toast.success("ID copied to clipboard");
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontFamily: "monospace", fontSize: 12, color: PreggaColors.neutral500 }}>
        {id.slice(0, 8)}...
      </span>
      <button
        onClick={handleCopy}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: 4,
          border: "none",
          background: "transparent",
          color: PreggaColors.neutral400,
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = PreggaColors.neutral100;
          e.currentTarget.style.color = PreggaColors.neutral600;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = PreggaColors.neutral400;
        }}
        title="Copy ID"
      >
        <Copy size={14} />
      </button>
    </div>
  );
}

export function DoulasView({ isMobile, subView, onNavigateToSubView, onGoBack, onNavigateToUserWithReturn, onNavigateToConversation }: DoulasViewProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<DoulaFilters>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [listTab, setListTab] = useState<"active" | "inactive">("active");
  const [isReactivating, setIsReactivating] = useState<string | null>(null);

  const {
    data: doulas,
    count,
    isLoading,
    error,
    page,
    pageSize,
    totalPages,
    setPage,
    refetch,
  } = useSupabasePaginatedQuery<DoulaWithProfile>(
    ['doulas', 'active', JSON.stringify(filters), searchQuery],
    (from, to) => fetchDoulas(from, to, { ...filters, search: searchQuery || undefined, isAvailable: true }),
    { pageSize: 10 }
  );

  const { data: inactiveDoulas, isLoading: inactiveLoading, refetch: refetchInactive } = useSupabaseQuery<DoulaWithProfile[]>(
    ['doulas', 'inactive'],
    fetchInactiveDoulas
  );

  const { data: doulaKpis, refetch: refetchDoulaKpis } = useSupabaseQuery(
    ['doulas', 'kpi-counts'],
    () => fetchDoulaKpiCounts()
  );

  const handleReactivate = async (doulaId: string) => {
    setIsReactivating(doulaId);
    try {
      await reactivateDoula(doulaId);
      toast.success("Doula reactivated successfully");
      refetchInactive();
      refetch();
      refetchDoulaKpis();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setIsReactivating(null);
    }
  };

  if (subView) {
    return (
      <DoulaDetailView
        doulaId={subView}
        isMobile={isMobile}
        onGoBack={onGoBack}
        onViewClient={onNavigateToUserWithReturn}
        onNavigateToConversation={onNavigateToConversation}
        onRefresh={() => {
          refetch();
          refetchInactive();
          refetchDoulaKpis();
        }}
      />
    );
  }

  const columns: TableColumn<DoulaWithProfile>[] = [
    {
      key: "id",
      label: "Doula ID",
      render: (_, row) => <CopyableId id={row.id} />,
    },
    {
      key: "display_name",
      label: "Doula",
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: PreggaColors.accent100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: PreggaColors.accent600,
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
            }}
          >
            {row.avatar_url ? (
              <img src={row.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              (row.display_name || "D").split(" ").map((n) => n[0]).join("").slice(0, 2)
            )}
          </div>
          <div>
            <div style={{ fontWeight: 500, color: PreggaColors.neutral900 }}>{row.display_name || "Unnamed"}</div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>{row.email || row.phone || "No contact"}</div>
          </div>
        </div>
      ),
    },
    {
      key: "doula_profiles",
      label: "Bio",
      render: (_, row) => (
        <span style={{ color: PreggaColors.neutral500, fontSize: 13 }}>
          {row.doula_profiles?.bio?.slice(0, 50) || "No bio"}
          {row.doula_profiles?.bio && row.doula_profiles.bio.length > 50 ? "..." : ""}
        </span>
      ),
    },
    {
      key: "availability",
      label: "Status",
      render: (_, row) => (
        <Badge variant={row.doula_profiles?.is_available ? "accent" : "neutral"} size="sm">
          {row.doula_profiles?.is_available ? "Available" : "Unavailable"}
        </Badge>
      ),
    },
    {
      key: "created_at",
      label: "Joined",
      render: (_, row) => (
        <span style={{ color: PreggaColors.neutral500, fontSize: 13 }}>{formatTimeAgo(row.created_at)}</span>
      ),
    },
  ];

  const hasActiveFilters = Object.values(filters).some(v => v !== undefined) || searchQuery !== "";

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({});
  };

  const renderMobileCard = (doula: DoulaWithProfile) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: PreggaColors.accent100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: PreggaColors.accent600,
              fontWeight: 600,
              fontSize: 14,
              overflow: "hidden",
            }}
          >
            {doula.avatar_url ? (
              <img src={doula.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              (doula.display_name || "D").split(" ").map((n) => n[0]).join("").slice(0, 2)
            )}
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14, color: PreggaColors.neutral900 }}>
              {doula.display_name || "Unnamed"}
            </div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>{doula.email || doula.phone}</div>
          </div>
        </div>
        <Badge variant={doula.doula_profiles?.is_available ? "accent" : "neutral"} size="sm">
          {doula.doula_profiles?.is_available ? "Available" : "Unavailable"}
        </Badge>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
        <span style={{ color: PreggaColors.neutral500 }}>Joined {formatTimeAgo(doula.created_at)}</span>
      </div>
    </div>
  );

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <AlertCircle size={48} color={PreggaColors.error500} style={{ marginBottom: 16 }} />
        <h3 style={{ color: PreggaColors.neutral900, marginBottom: 8 }}>Failed to load doulas</h3>
        <p style={{ color: PreggaColors.neutral500 }}>{friendlyError(error)}</p>
        <Button onClick={refetch} style={{ marginTop: 16 }}>Try Again</Button>
      </div>
    );
  }

  const kpiTotal = doulaKpis?.total ?? count;
  const kpiAvailable = doulaKpis?.available ?? 0;

  const inactiveColumns: TableColumn<DoulaWithProfile>[] = [
    {
      key: "id",
      label: "Doula ID",
      render: (_, row) => <CopyableId id={row.id} />,
    },
    {
      key: "display_name",
      label: "Doula",
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: PreggaColors.neutral100, display: "flex", alignItems: "center", justifyContent: "center", color: PreggaColors.neutral500, fontSize: 13, fontWeight: 600, overflow: "hidden" }}>
            {row.avatar_url ? (
              <img src={row.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              (row.display_name || "D").split(" ").map((n) => n[0]).join("").slice(0, 2)
            )}
          </div>
          <div>
            <div style={{ fontWeight: 500, color: PreggaColors.neutral900 }}>{row.display_name || "Unnamed"}</div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>{row.email || row.phone || "No contact"}</div>
          </div>
        </div>
      ),
    },
    {
      key: "bio",
      label: "Bio",
      render: (_, row) => (
        <span style={{ color: PreggaColors.neutral600, fontSize: 13 }}>
          {row.doula_profiles?.bio?.slice(0, 50) || "—"}{row.doula_profiles?.bio && row.doula_profiles.bio.length > 50 ? "..." : ""}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: () => <Badge variant="neutral" size="sm">Inactive</Badge>,
    },
    {
      key: "created_at",
      label: "Joined",
      render: (_, row) => (
        <span style={{ color: PreggaColors.neutral500, fontSize: 13 }}>{formatTimeAgo(row.created_at)}</span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (_, row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleReactivate(row.id);
          }}
          disabled={isReactivating === row.id}
          icon={<RotateCcw size={14} />}
        >
          {isReactivating === row.id ? "..." : "Reactivate"}
        </Button>
      ),
    },
  ];

  const renderInactiveMobileCard = (doula: DoulaWithProfile) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: PreggaColors.neutral100, display: "flex", alignItems: "center", justifyContent: "center", color: PreggaColors.neutral500, fontWeight: 600, fontSize: 14, overflow: "hidden" }}>
            {doula.avatar_url ? (
              <img src={doula.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              (doula.display_name || "D").split(" ").map((n) => n[0]).join("").slice(0, 2)
            )}
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14, color: PreggaColors.neutral900 }}>
              {doula.display_name || "Unnamed"}
            </div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>{doula.email || doula.phone}</div>
          </div>
        </div>
        <Badge variant="neutral" size="sm">Inactive</Badge>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: PreggaColors.neutral500, fontSize: 13 }}>Joined {formatTimeAgo(doula.created_at)}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleReactivate(doula.id);
          }}
          disabled={isReactivating === doula.id}
          icon={<RotateCcw size={14} />}
        >
          {isReactivating === doula.id ? "..." : "Reactivate"}
        </Button>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${PreggaColors.neutral200}` }}>
        <button
          onClick={() => setListTab("active")}
          style={{
            padding: "12px 20px",
            border: "none",
            borderBottom: listTab === "active" ? `2px solid ${PreggaColors.accent500}` : "2px solid transparent",
            background: "transparent",
            color: listTab === "active" ? PreggaColors.accent700 : PreggaColors.neutral500,
            fontSize: 14,
            fontWeight: listTab === "active" ? 600 : 400,
            cursor: "pointer",
            fontFamily: "inherit",
            marginBottom: -1,
          }}
        >
          Active Doulas
        </button>
        <button
          onClick={() => setListTab("inactive")}
          style={{
            padding: "12px 20px",
            border: "none",
            borderBottom: listTab === "inactive" ? `2px solid ${PreggaColors.accent500}` : "2px solid transparent",
            background: "transparent",
            color: listTab === "inactive" ? PreggaColors.accent700 : PreggaColors.neutral500,
            fontSize: 14,
            fontWeight: listTab === "inactive" ? 600 : 400,
            cursor: "pointer",
            fontFamily: "inherit",
            marginBottom: -1,
          }}
        >
          Inactive ({inactiveDoulas?.length ?? 0})
        </button>
      </div>

      {/* Filters - only for active tab */}
      {listTab === "active" && isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={<Search size={16} />}
                showClear
                onClear={() => setSearchQuery("")}
                style={{ marginBottom: 0 }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Select
                value={filters.isAvailable === true ? "yes" : filters.isAvailable === false ? "no" : ""}
                onChange={(v) => setFilters({ ...filters, isAvailable: v === "yes" ? true : v === "no" ? false : undefined })}
                options={[
                  { value: "", label: "All Availability" },
                  { value: "yes", label: "Available" },
                  { value: "no", label: "Unavailable" },
                ]}
              />
            </div>
            {hasActiveFilters && (
              <button onClick={clearAllFilters} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 42, height: 42, borderRadius: 8, border: `1px solid ${PreggaColors.neutral200}`, background: PreggaColors.white, color: PreggaColors.neutral500, cursor: "pointer" }}>
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      ) : listTab === "active" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 280 }}>
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search size={16} />}
              showClear
              onClear={() => setSearchQuery("")}
              style={{ marginBottom: 0 }}
            />
          </div>
          <div style={{ width: 150 }}>
            <Select
              value={filters.isAvailable === true ? "yes" : filters.isAvailable === false ? "no" : ""}
              onChange={(v) => setFilters({ ...filters, isAvailable: v === "yes" ? true : v === "no" ? false : undefined })}
              options={[
                { value: "", label: "All Availability" },
                { value: "yes", label: "Available" },
                { value: "no", label: "Unavailable" },
              ]}
            />
          </div>
          {hasActiveFilters && (
            <button onClick={clearAllFilters} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 42, padding: "0 14px", borderRadius: 8, border: `1px solid ${PreggaColors.neutral200}`, background: PreggaColors.white, color: PreggaColors.neutral700, fontSize: 14, fontFamily: "'Inter', sans-serif", cursor: "pointer" }}>
              <X size={14} />
              Clear
            </button>
          )}
          <div style={{ marginLeft: "auto" }}>
            <Button icon={<Plus size={16} />} onClick={() => setShowAddModal(true)}>
              Add Doula
            </Button>
          </div>
        </div>
      ) : null}

      {/* Stats - only for active tab */}
      {listTab === "active" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 16 }}>
          <StatCard label="Total Doulas" value={kpiTotal} icon={<Users size={18} />} color={PreggaColors.accent500} delay={0} />
          <StatCard label="Available" value={kpiAvailable} icon={<Check size={18} />} color={PreggaColors.accent500} delay={100} />
        </div>
      )}

      {/* Mobile Add Button - only for active tab */}
      {listTab === "active" && isMobile && (
        <Button icon={<Plus size={16} />} onClick={() => setShowAddModal(true)} style={{ width: "100%" }}>
          Add Doula
        </Button>
      )}

      {/* Data Table */}
      {listTab === "active" ? (
        <DataTable
          columns={columns}
          data={doulas}
          currentPage={page}
          totalPages={totalPages}
          totalItems={count}
          pageSize={pageSize}
          onPageChange={setPage}
          onRowClick={(row) => onNavigateToSubView?.(row.id)}
          emptyMessage="No doulas found"
          isMobile={isMobile}
          mobileCardRender={renderMobileCard}
          isLoading={isLoading}
        />
      ) : (
        <DataTable
          columns={inactiveColumns}
          data={inactiveDoulas || []}
          currentPage={1}
          totalPages={1}
          totalItems={inactiveDoulas?.length || 0}
          pageSize={50}
          onPageChange={() => {}}
          onRowClick={(row) => onNavigateToSubView?.(row.id)}
          emptyMessage="No inactive doulas"
          isMobile={isMobile}
          mobileCardRender={renderInactiveMobileCard}
          isLoading={inactiveLoading}
        />
      )}

      {/* Add Doula Modal */}
      <AddDoulaModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          refetch();
          refetchDoulaKpis();
        }}
      />
    </div>
  );
}

function DoulaDetailView({
  doulaId,
  isMobile,
  onGoBack,
  onViewClient,
  onNavigateToConversation,
  onRefresh,
}: {
  doulaId: string;
  isMobile: boolean;
  onGoBack?: () => void;
  onViewClient?: (clientId: string) => void;
  onNavigateToConversation?: (conversationId: string) => void;
  onRefresh?: () => void;
}) {
  const [activeTab, setActiveTab] = useState<"profile" | "availability" | "clients" | "conversations" | "deactivate">("profile");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [manageClient, setManageClient] = useState<{ id: string; name: string } | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [deactivateConfirmText, setDeactivateConfirmText] = useState("");
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isTogglingAvailability, setIsTogglingAvailability] = useState(false);
  const [localAvailability, setLocalAvailability] = useState<boolean | null>(null);

  const { data: doula, isLoading, error, refetch } = useSupabaseQuery<DoulaWithProfile | null>(
    ['doula', doulaId],
    () => fetchDoulaById(doulaId)
  );

  const { data: assignments, refetch: refetchAssignments } = useSupabaseQuery<DoulaAssignmentWithUser[]>(
    ['doula', doulaId, 'assignments'],
    () => fetchDoulaAssignments(doulaId),
    { enabled: activeTab === 'clients' }
  );

  const { data: conversations } = useSupabaseQuery<ConversationWithUsers[]>(
    ['doula', doulaId, 'conversations'],
    () => fetchDoulaConversations(doulaId),
    { enabled: activeTab === 'conversations' }
  );

  // Use local state if set, otherwise use server data
  const isAvailable = localAvailability !== null ? localAvailability : (doula?.doula_profiles?.is_available ?? false);

  const handleToggleAvailability = async () => {
    if (!doula || isTogglingAvailability) return;
    const newAvailability = !isAvailable;
    
    // Optimistically update local state immediately
    setLocalAvailability(newAvailability);
    setIsTogglingAvailability(true);
    
    try {
      await updateDoulaAvailability(doula.id, newAvailability);
      toast.success(newAvailability ? "Doula marked as available" : "Doula marked as unavailable");
      // Silently refresh data in background so list stays in sync
      refetch();
      onRefresh?.();
    } catch (err) {
      // Revert on error
      setLocalAvailability(!newAvailability);
      toast.error(friendlyError(err));
    } finally {
      setIsTogglingAvailability(false);
    }
  };

  const handleDeactivate = async () => {
    if (!doula || deactivateConfirmText !== "DEACTIVATE") return;
    setIsDeactivating(true);
    try {
      await deactivateDoula(doula.id);
      toast.success("Doula account deactivated");
      setShowDeactivateModal(false);
      setDeactivateConfirmText("");
      onRefresh?.();
      onGoBack?.();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setIsDeactivating(false);
    }
  };

  if (isLoading) {
    return <div style={{ padding: 40, textAlign: "center" }}>Loading...</div>;
  }

  if (error || !doula) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <AlertCircle size={48} color={PreggaColors.error500} style={{ marginBottom: 16 }} />
        <h3 style={{ color: PreggaColors.neutral900, marginBottom: 8 }}>Doula not found</h3>
        <Button onClick={onGoBack} style={{ marginTop: 16 }}>Go Back</Button>
      </div>
    );
  }

  const tabs: Tab[] = [
    { id: "profile", label: "Profile", icon: <User size={15} /> },
    { id: "availability", label: "Availability", icon: <ToggleRight size={15} /> },
    { id: "clients", label: "Clients", icon: <Users size={15} /> },
    { id: "conversations", label: "Conversations", icon: <MessageCircle size={15} /> },
    { id: "deactivate", label: doula.doula_profiles?.is_available === false ? "Reactivate" : "Deactivate", icon: doula.doula_profiles?.is_available === false ? <RotateCcw size={15} /> : <Trash2 size={15} /> },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Header */}
      <DetailHeader
        title={doula.display_name || "Doula Profile"}
        subtitle={doula.email || doula.phone || "No contact info"}
        avatarUrl={doula.avatar_url}
        avatarFallback={doula.display_name || "Doula"}
        avatarGradient={isAvailable 
          ? [PreggaColors.accent400, PreggaColors.accent500] 
          : [PreggaColors.neutral400, PreggaColors.neutral500]}
        onGoBack={() => onGoBack?.()}
        action={<Button icon={<Pencil size={15} />} onClick={() => setShowEditModal(true)} size="sm">Edit</Button>}
        stats={[
          { label: "Status", value: isAvailable ? "Available" : "Unavailable", highlight: isAvailable },
          { label: "Joined", value: formatDate(doula.created_at) },
        ]}
        isMobile={isMobile}
        accentColor={isAvailable 
          ? `linear-gradient(90deg, ${PreggaColors.accent500} 0%, ${PreggaColors.accent400} 100%)` 
          : `linear-gradient(90deg, ${PreggaColors.neutral500} 0%, ${PreggaColors.neutral400} 100%)`}
      />

      {/* Tabs */}
      <TabSelector
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as typeof activeTab)}
        isMobile={isMobile}
      />

      {/* Tab Content */}
      {activeTab === "profile" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          <InfoCard label="Name" value={doula.display_name || "—"} />
          <InfoCard label="Email" value={doula.email || "—"} />
          <InfoCard label="Phone" value={doula.phone || "—"} />
          <InfoCard label="Years of Experience" value={doula.doula_profiles?.years_experience ? `${doula.doula_profiles.years_experience} years` : "—"} />
          <InfoCard label="Online Status" value={doula.doula_profiles?.is_online ? "Online" : "Offline"} />
          <InfoCard label="Last Seen" value={doula.doula_profiles?.last_seen_at ? formatDate(doula.doula_profiles.last_seen_at) : "Never"} />
          <InfoCard label="Auth Provider" value={doula.auth_provider || "—"} />
          <InfoCard label="Created" value={formatDate(doula.created_at)} />
          <InfoCard label="Last Updated" value={formatDate(doula.updated_at)} />
          
          {/* Expertise Tags */}
          <Card padding="16px" style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
            <div style={{ fontSize: 13, color: PreggaColors.neutral500, marginBottom: 8 }}>Expertise</div>
            {doula.doula_profiles?.expertise && doula.doula_profiles.expertise.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {doula.doula_profiles.expertise.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 12px",
                      background: PreggaColors.accent50,
                      color: PreggaColors.accent700,
                      borderRadius: 16,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 14, color: PreggaColors.neutral400 }}>No expertise tags</div>
            )}
          </Card>

          {/* Bio */}
          <Card padding="16px" style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
            <div style={{ fontSize: 13, color: PreggaColors.neutral500, marginBottom: 4 }}>Bio</div>
            <div style={{ fontSize: 14, color: PreggaColors.neutral900, lineHeight: 1.5 }}>
              {doula.doula_profiles?.bio || "No bio provided"}
            </div>
          </Card>
        </div>
      )}

      {activeTab === "availability" && (
        <Card padding="24px">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: PreggaColors.neutral900, margin: "0 0 4px" }}>
                Availability Status
              </h3>
              <p style={{ fontSize: 14, color: PreggaColors.neutral500, margin: 0 }}>
                Control whether this doula appears as available for new clients
              </p>
            </div>
            {/* Toggle Switch */}
            <button
              onClick={handleToggleAvailability}
              disabled={isTogglingAvailability}
              style={{
                position: "relative",
                width: 56,
                height: 30,
                borderRadius: 15,
                border: "none",
                outline: "none",
                background: isAvailable ? PreggaColors.accent500 : PreggaColors.neutral300,
                cursor: isTogglingAvailability ? "wait" : "pointer",
                transition: "background 0.2s ease",
                padding: 0,
                opacity: isTogglingAvailability ? 0.7 : 1,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 3,
                  left: isAvailable ? 29 : 3,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: PreggaColors.white,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                  transition: "left 0.2s ease",
                }}
              />
            </button>
          </div>
          
          {/* Status indicator */}
          <div style={{ 
            display: "inline-flex", 
            alignItems: "center", 
            gap: 8, 
            padding: "8px 16px", 
            borderRadius: 20,
            background: isAvailable ? PreggaColors.accent50 : PreggaColors.neutral100,
            marginBottom: 20,
          }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: isAvailable ? PreggaColors.accent500 : PreggaColors.neutral400,
            }} />
            <span style={{ 
              fontSize: 14, 
              fontWeight: 500, 
              color: isAvailable ? PreggaColors.accent700 : PreggaColors.neutral600
            }}>
              {isAvailable ? "Available" : "Unavailable"}
            </span>
          </div>

          <div style={{ padding: "16px", background: PreggaColors.neutral50, borderRadius: 8 }}>
            <div style={{ fontSize: 13, color: PreggaColors.neutral500, marginBottom: 4 }}>Push Token</div>
            <div style={{ fontSize: 14, color: PreggaColors.neutral700, wordBreak: "break-all" }}>
              {doula.push_token || "No push token registered"}
            </div>
          </div>
        </Card>
      )}

      {activeTab === "clients" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Assign Client button */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button icon={<UserPlus size={15} />} onClick={() => setShowAssignModal(true)} size="sm">
              Assign Client
            </Button>
          </div>

          {/* Assigned Clients */}
          {assignments && assignments.length > 0 && (
            <Card padding="0">
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${PreggaColors.neutral100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: PreggaColors.neutral900, display: "flex", alignItems: "center", gap: 8 }}>
                  <Link2 size={15} />
                  Assigned Clients ({assignments.length})
                </h4>
              </div>
              <div>
                {assignments.map((assignment, idx) => {
                  const user = assignment.user;
                  return (
                    <div
                      key={assignment.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "14px 20px",
                        borderBottom: idx < assignments.length - 1 ? `1px solid ${PreggaColors.neutral100}` : "none",
                        cursor: "pointer",
                      }}
                      onClick={() => onViewClient?.(user.id)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: PreggaColors.primary100, display: "flex", alignItems: "center", justifyContent: "center", color: PreggaColors.primary600, fontWeight: 600, fontSize: 14, overflow: "hidden" }}>
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            (user.display_name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2)
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 14, color: PreggaColors.neutral900 }}>{user.display_name || "Unnamed"}</div>
                          <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>
                            {user.email || user.phone || "No contact"}
                            {assignment.assigned_at ? ` · Assigned ${formatDate(assignment.assigned_at)}` : ""}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Badge variant="sage" size="sm">Assigned</Badge>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setManageClient({ id: user.id, name: user.display_name || "this client" });
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: PreggaColors.neutral400,
                            padding: 6,
                            borderRadius: 6,
                            display: "flex",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = PreggaColors.error500; e.currentTarget.style.background = PreggaColors.error50; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = PreggaColors.neutral400; e.currentTarget.style.background = "none"; }}
                          title="Manage client"
                        >
                          <UserMinus size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Empty state */}
          {(!assignments || assignments.length === 0) && (
            <Card padding="48px">
              <div style={{ textAlign: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: PreggaColors.neutral100, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                  <Users size={24} color={PreggaColors.neutral400} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 500, color: PreggaColors.neutral600 }}>No clients yet</div>
                <div style={{ fontSize: 13, color: PreggaColors.neutral400, marginTop: 4, marginBottom: 16 }}>Assign clients to this doula so they can broadcast</div>
                <Button icon={<UserPlus size={15} />} onClick={() => setShowAssignModal(true)} size="sm">
                  Assign Client
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === "conversations" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {conversations && conversations.length > 0 ? (
            conversations.map((conv) => (
              <Card 
                key={conv.id} 
                padding="16px"
                style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                onClick={() => onNavigateToConversation?.(conv.id)}
              >
                <div 
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  onMouseEnter={(e) => (e.currentTarget.parentElement as HTMLElement).style.background = PreggaColors.neutral50}
                  onMouseLeave={(e) => (e.currentTarget.parentElement as HTMLElement).style.background = PreggaColors.white}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: PreggaColors.accent100, display: "flex", alignItems: "center", justifyContent: "center", color: PreggaColors.accent600 }}>
                      <MessageCircle size={18} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14, color: PreggaColors.neutral900 }}>
                        Chat with {conv.pregnant_user?.display_name || "User"}
                      </div>
                      <div style={{ fontSize: 13, color: PreggaColors.neutral500 }}>
                        Started {conv.started_at ? formatTimeAgo(conv.started_at) : "Unknown"}
                      </div>
                    </div>
                  </div>
                  <StatusBadge status={conv.is_active ? "active" : "ended"} />
                </div>
              </Card>
            ))
          ) : (
            <Card padding="40px">
              <div style={{ textAlign: "center", color: PreggaColors.neutral400 }}>
                <MessageCircle size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
                <div>No conversations</div>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === "deactivate" && (
        <Card padding="24px">
          {doula.doula_profiles?.is_available === false ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: 20, background: PreggaColors.accent50, borderRadius: 12, border: `1px solid ${PreggaColors.accent100}` }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: PreggaColors.white, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <RotateCcw size={22} color={PreggaColors.accent600} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900, margin: "0 0 6px" }}>
                  Reactivate Doula Account
                </h3>
                <p style={{ fontSize: 13, color: PreggaColors.neutral600, margin: "0 0 16px", lineHeight: 1.5 }}>
                  This doula account is currently deactivated. Reactivating will make them available
                  again and they will appear in search results for users.
                </p>
                <button
                  onClick={async () => {
                    try {
                      await reactivateDoula(doula.id);
                      toast.success("Doula reactivated successfully");
                      refetch();
                      onRefresh?.();
                    } catch (err) {
                      toast.error(friendlyError(err));
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: 8,
                    border: `1px solid ${PreggaColors.accent500}`,
                    color: PreggaColors.white,
                    background: PreggaColors.accent500,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = PreggaColors.accent600;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = PreggaColors.accent500;
                  }}
                >
                  <RotateCcw size={14} />
                  Reactivate Account
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: 20, background: PreggaColors.error50, borderRadius: 12, border: `1px solid ${PreggaColors.error100}` }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: PreggaColors.white, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AlertTriangle size={22} color={PreggaColors.error500} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900, margin: "0 0 6px" }}>
                  Deactivate Doula Account
                </h3>
                <p style={{ fontSize: 13, color: PreggaColors.neutral600, margin: "0 0 16px", lineHeight: 1.5 }}>
                  Deactivating this doula will mark them as unavailable and unassign all linked clients.
                  They will no longer appear in search results for users. This action can be reversed.
                </p>
                <button
                  onClick={() => setShowDeactivateModal(true)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: 8,
                    border: `1px solid ${PreggaColors.error400}`,
                    color: PreggaColors.error600,
                    background: PreggaColors.white,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = PreggaColors.error50;
                    e.currentTarget.style.borderColor = PreggaColors.error500;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = PreggaColors.white;
                    e.currentTarget.style.borderColor = PreggaColors.error400;
                  }}
                >
                  <Trash2 size={14} />
                  Deactivate Account
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Deactivate Modal with DELETE verification */}
      <Modal
        open={showDeactivateModal}
        onClose={() => { setShowDeactivateModal(false); setDeactivateConfirmText(""); }}
        title=""
        width={420}
        footer={
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => { setShowDeactivateModal(false); setDeactivateConfirmText(""); }}>Cancel</Button>
            <Button
              onClick={handleDeactivate}
              loading={isDeactivating}
              disabled={deactivateConfirmText !== "DEACTIVATE"}
              style={{
                background: PreggaColors.error500,
                opacity: deactivateConfirmText === "DEACTIVATE" ? 1 : 0.5,
                cursor: deactivateConfirmText === "DEACTIVATE" ? "pointer" : "not-allowed",
              }}
            >
              Deactivate Account
            </Button>
          </div>
        }
      >
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: PreggaColors.error50, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <AlertTriangle size={28} color={PreggaColors.error500} />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: PreggaColors.neutral900, margin: "0 0 8px" }}>
            Deactivate Doula Account?
          </h3>
          <p style={{ fontSize: 14, color: PreggaColors.neutral500, lineHeight: 1.6, margin: "0 0 20px" }}>
            Are you sure you want to deactivate <strong style={{ color: PreggaColors.neutral700 }}>{doula.display_name || "this doula"}</strong>?
            They will be marked as unavailable and removed from search results.
          </p>
          <div style={{ textAlign: "left" }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 8 }}>
              Type <span style={{ fontFamily: "monospace", background: PreggaColors.neutral100, padding: "2px 6px", borderRadius: 4, color: PreggaColors.error600 }}>DEACTIVATE</span> to confirm
            </label>
            <input
              type="text"
              value={deactivateConfirmText}
              onChange={(e) => setDeactivateConfirmText(e.target.value.toUpperCase())}
              placeholder="DEACTIVATE"
              style={{
                width: "100%",
                padding: "12px 14px",
                fontSize: 14,
                border: `1px solid ${deactivateConfirmText === "DEACTIVATE" ? PreggaColors.error400 : PreggaColors.neutral200}`,
                borderRadius: 8,
                outline: "none",
                fontFamily: "monospace",
                letterSpacing: "1px",
                boxSizing: "border-box",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = PreggaColors.error400;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${PreggaColors.error100}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = deactivateConfirmText === "DEACTIVATE" ? PreggaColors.error400 : PreggaColors.neutral200;
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <EditDoulaModal
        doula={doula}
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={async (data) => {
          try {
            await updateDoulaProfile(doula.id, data.profile, data.doula ?? undefined);
            toast.success("Profile updated");
            refetch();
            setShowEditModal(false);
          } catch (err) {
            toast.error(friendlyError(err));
          }
        }}
      />

      <AssignClientsModal
        open={showAssignModal}
        onClose={() => { setShowAssignModal(false); refetchAssignments(); }}
        doulaId={doulaId}
        doulaName={doula.display_name || "this doula"}
      />

      {/* Manage Client Modal */}
      <Modal
        open={!!manageClient}
        onClose={() => { setManageClient(null); setIsUnassigning(false); }}
        title="Manage Client"
        width={440}
        footer={
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => setManageClient(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!manageClient) return;
                setIsUnassigning(true);
                try {
                  await unassignClientFromDoula(doulaId, manageClient.id);
                  toast.success("Client unassigned successfully");
                  setManageClient(null);
                  refetchAssignments();
                } catch (err) {
                  toast.error(friendlyError(err));
                } finally {
                  setIsUnassigning(false);
                }
              }}
              loading={isUnassigning}
              style={{ background: PreggaColors.error500 }}
            >
              Unassign Client
            </Button>
          </div>
        }
      >
        {manageClient && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: PreggaColors.neutral500, marginBottom: 6 }}>Client</div>
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 10,
                  background: PreggaColors.neutral50,
                  border: `1px solid ${PreggaColors.neutral200}`,
                  fontSize: 15,
                  fontWeight: 500,
                  color: PreggaColors.neutral900,
                }}
              >
                {manageClient.name}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: PreggaColors.neutral500, marginBottom: 6 }}>Doula</div>
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 10,
                  background: PreggaColors.neutral50,
                  border: `1px solid ${PreggaColors.neutral200}`,
                  fontSize: 15,
                  fontWeight: 500,
                  color: PreggaColors.neutral900,
                }}
              >
                {doula.display_name || "Unknown"}
              </div>
            </div>

            <div
              style={{
                padding: "14px 16px",
                background: PreggaColors.error50,
                borderRadius: 10,
                border: `1px solid ${PreggaColors.error100}`,
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <AlertTriangle size={18} color={PreggaColors.error500} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13, color: PreggaColors.neutral700, lineHeight: 1.5 }}>
                This will remove <strong>{manageClient.name}</strong> from <strong>{doula.display_name || "this doula"}</strong>'s assigned clients.
                The client will no longer be able to broadcast to this doula.
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <Card padding="16px">
      <div style={{ fontSize: 13, color: PreggaColors.neutral500, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: PreggaColors.neutral900 }}>{value}</div>
    </Card>
  );
}

function StatCard({ label, value, icon, color, delay = 0 }: { label: string; value: number; icon: React.ReactNode; color: string; delay?: number }) {
  const animatedValue = useCountUp(value, 1500, delay);

  return (
    <div style={{ background: PreggaColors.white, borderRadius: 12, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: PreggaColors.neutral500 }}>{label}</span>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", color }}>
          {icon}
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: PreggaColors.neutral900 }}>{animatedValue}</div>
    </div>
  );
}

function EditDoulaModal({
  doula,
  open,
  onClose,
  onSave,
}: {
  doula: DoulaWithProfile;
  open: boolean;
  onClose: () => void;
  onSave: (data: { profile: Partial<DoulaWithProfile>; doula?: Partial<DoulaWithProfile['doula_profiles']> }) => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    display_name: doula.display_name || "",
    email: doula.email || "",
    phone: doula.phone || "",
    bio: doula.doula_profiles?.bio || "",
    years_experience: doula.doula_profiles?.years_experience || 0,
    expertise: doula.doula_profiles?.expertise || [] as string[],
  });
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const getInputStyle = (fieldName: string): React.CSSProperties => ({
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    border: `1px solid ${focusedField === fieldName ? PreggaColors.accent400 : PreggaColors.neutral200}`,
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    outline: "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    boxShadow: focusedField === fieldName ? `0 0 0 3px ${PreggaColors.accent100}` : "none",
  });

  useEffect(() => {
    if (open) {
      setFormData({
        display_name: doula.display_name || "",
        email: doula.email || "",
        phone: doula.phone || "",
        bio: doula.doula_profiles?.bio || "",
        years_experience: doula.doula_profiles?.years_experience || 0,
        expertise: doula.doula_profiles?.expertise || [],
      });
      setNewTag("");
    }
  }, [open, doula]);

  const handleAddTag = () => {
    const tag = newTag.trim();
    if (tag && !formData.expertise.includes(tag)) {
      setFormData({ ...formData, expertise: [...formData.expertise, tag] });
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({ ...formData, expertise: formData.expertise.filter(t => t !== tagToRemove) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        profile: {
          display_name: formData.display_name || null,
          email: formData.email || null,
          phone: formData.phone || null,
        },
        doula: { 
          bio: formData.bio || null,
          years_experience: formData.years_experience || null,
          expertise: formData.expertise.length > 0 ? formData.expertise : null,
        },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Doula Profile"
      width={520}
      footer={
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>Save Changes</Button>
        </div>
      }
    >
      <Input label="Name" value={formData.display_name} onChange={(e) => setFormData({ ...formData, display_name: e.target.value })} />
      <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
      <Input label="Phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
      
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 6 }}>
          Years of Experience
        </label>
        <input
          type="number"
          min={0}
          max={50}
          value={formData.years_experience}
          onChange={(e) => setFormData({ ...formData, years_experience: parseInt(e.target.value) || 0 })}
          onFocus={() => setFocusedField("years_experience")}
          onBlur={() => setFocusedField(null)}
          style={getInputStyle("years_experience")}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 6 }}>
          Expertise Tags
        </label>
        
        {/* Current Tags */}
        {formData.expertise.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            {formData.expertise.map((tag, index) => (
              <span
                key={index}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 10px",
                  background: PreggaColors.accent50,
                  color: PreggaColors.accent700,
                  borderRadius: 16,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "none",
                    background: PreggaColors.accent200,
                    color: PreggaColors.accent700,
                    cursor: "pointer",
                    fontSize: 12,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        
        {/* Add New Tag */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddTag();
              }
            }}
            onFocus={() => setFocusedField("newTag")}
            onBlur={() => setFocusedField(null)}
            placeholder="Type a tag and press Enter"
            style={{ ...getInputStyle("newTag"), flex: 1, width: "auto" }}
          />
          <Button type="button" variant="outline" onClick={handleAddTag} size="sm">
            Add
          </Button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 6 }}>Bio</label>
        <textarea
          value={formData.bio}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          onFocus={() => setFocusedField("bio")}
          onBlur={() => setFocusedField(null)}
          rows={4}
          style={{
            ...getInputStyle("bio"),
            resize: "vertical",
          }}
        />
      </div>
    </Modal>
  );
}
